import {
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
  FormArray,
  FormControl
} from '@angular/forms';
import * as moment from 'moment';

/**
 * Verifies that the date represented by `endProp` is strictly after the date represented by `startProp`
 * @param startProp the FormGroup control name for start date
 * @param endProp the FormGroup control name for end date
 */
export const DaterangeValidator = (startProp: string, endProp: string): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control && startProp && endProp) {
      const val = control.value;
      if (
        val[endProp] &&
        val[startProp] &&
        moment.utc(val[endProp]).isBefore(moment.utc(val[startProp]))
      ) {
        return { invalidDateRange: true };
      }
    }
    return null;
  };
};

/**
 * Verifies that a given FormArray has a continuous range of dates that never overlap each other.
 *
 * NOTE: assumes the dates are valid (according to DaterangeValidator) and are in Chronological order
 * @param startDateKey the FormGroup control name for start date
 * @param endDateKey the FormGroup control name for end date
 */
export const NoOverlappingDatesValidator = <T extends { [key: string]: string | null }>(
  startDateKey: keyof T,
  endDateKey: keyof T,
  ignoredControlValue?: FormControl
): ValidatorFn => {
  return (control: FormArray) => {
    const controls = [...control.controls]
      .reverse()
      .filter(it => it !== ignoredControlValue && !it.disabled);
    if (controls.length === 0) {
      return null;
    }

    //Sort by start date
    controls.sort((cA, cB) => {
      const cAStartDateStr: string | null = cA.value[startDateKey];
      const cBStartDateStr: string | null = cB.value[startDateKey];
      if (cAStartDateStr == null && cBStartDateStr == null) {
        return 0;
      }
      if (cAStartDateStr != null && cBStartDateStr == null) {
        return -1;
      }
      if (cAStartDateStr == null && cBStartDateStr != null) {
        return 1;
      }
      //neither are null
      const cAStartDate = moment.utc(cAStartDateStr as string);
      const cBStartDate = moment.utc(cBStartDateStr as string);
      return cAStartDate.isBefore(cBStartDate) ? -1 : cAStartDate.isAfter(cBStartDate) ? 1 : 0;
    });

    // Here, we cycle through the items as a sliding window of pairs.
    // e.g.
    //    [1,2,3,4,5] => [1,2], [2,3], [3,4], [4,5]
    for (let idx = 0; idx < controls.length; idx++) {
      const lastValue: T | null = idx > 0 ? controls[idx - 1].value : null;
      const currentValue: T = controls[idx].value;
      const currentEndDate: string | null = currentValue[endDateKey];
      const currentStartDate: string | null = currentValue[startDateKey];

      if (currentStartDate === null) {
        return { customMessage: 'A required start date is missing' };
      }

      const currentStartDateMoment = moment.utc(currentStartDate);

      if (!!currentEndDate && moment.utc(currentEndDate).isBefore(currentStartDateMoment)) {
        return { customMessage: 'An end date occurs before a start date' };
      }

      if (lastValue) {
        const lastEndDate = lastValue[endDateKey] as string | null;
        if (lastEndDate === null) {
          // The end date is not required for the newest record (could be "current")
          return { customMessage: 'A required end date is missing' };
        }

        const lastEndDateMoment = moment.utc(lastEndDate);
        if (currentStartDateMoment.startOf('day').isSameOrBefore(lastEndDateMoment.endOf('day'))) {
          return { customMessage: 'A date ranges overlaps' };
        }
      }
    }

    return null;
  };
};
